import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { queryKeys } from '@/lib/query';
import type {
  SystemList,
  ZabbixHostListResponse,
  ZabbixIngestRunResponse,
  ZabbixIntegrationStatus,
  ZabbixUnmatchedResponse,
} from '@/types/api';

/**
 * Zabbix-Integration (Spec S8-03): Status-Karte + Unmatched-Report in den
 * Integrations-Settings.
 *
 * Typen kommen seit dem OpenAPI-Nachtrag aus `pnpm gen:types` und werden
 * hier nur re-exportiert (Aliase in types/api.ts), damit Konsumenten wie
 * routes/settings/integrations.tsx ihre Imports behalten.
 */

export type {
  ZabbixHostListResponse,
  ZabbixHostOption,
  ZabbixIngestRunResponse,
  ZabbixIngestStatusValue,
  ZabbixIntegrationStatus,
  ZabbixOrphanedOverride,
  ZabbixSystemCandidate,
  ZabbixUnmatchedHost,
  ZabbixUnmatchedReason,
  ZabbixUnmatchedResponse,
} from '@/types/api';

// --- Query-Keys (lokal; lib/query.ts kennt die Integration noch nicht) -----

export const zabbixIntegrationKeys = {
  status: ['zabbix-integration', 'status'] as const,
  unmatched: ['zabbix-integration', 'unmatched'] as const,
  assignableSystems: (search: string) =>
    ['zabbix-integration', 'assignable-systems', search] as const,
  hosts: (filter: Record<string, unknown>) =>
    ['zabbix-integration', 'hosts', filter] as const,
};

// --- Queries ---------------------------------------------------------------

/** Status-Karte: Auto-Refresh alle 30s (Muster useSyncStatus). */
export function useZabbixIntegrationStatus(): ReturnType<
  typeof useQuery<ZabbixIntegrationStatus>
> {
  return useQuery<ZabbixIntegrationStatus>({
    queryKey: zabbixIntegrationKeys.status,
    queryFn: () => api.get<ZabbixIntegrationStatus>('/integrations/zabbix/status'),
    refetchInterval: 30_000,
  });
}

/**
 * Unmatched-Report (Live-Dry-Run gegen Zabbix, schreibt nichts).
 * 503 = Zabbix nicht erreichbar — Retry bringt nichts (Muster
 * useZabbixPackages).
 */
export function useZabbixUnmatched(
  opts: { enabled?: boolean } = {}
): ReturnType<typeof useQuery<ZabbixUnmatchedResponse>> {
  return useQuery<ZabbixUnmatchedResponse>({
    queryKey: zabbixIntegrationKeys.unmatched,
    queryFn: () => api.get<ZabbixUnmatchedResponse>('/integrations/zabbix/unmatched'),
    enabled: opts.enabled ?? true,
    staleTime: 60_000,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status === 503) return false;
      return failureCount < 1;
    },
  });
}

// --- Zuordnen-Combobox: serverseitige System-Suche -------------------------

/** Kleiner Debounce-Helfer (für die Sucheingabe der Zuordnen-Combobox). */
// Der Helfer ist generisch und liegt deshalb seit 18.08.2026 in
// hooks/useDebouncedValue. Re-Export, damit bestehende Importe hier weiter
// funktionieren.
export { useDebouncedValue } from '@/hooks/useDebouncedValue';

export interface AssignableSystemOption {
  value: string;
  label: string;
  hint?: string | undefined;
}

export interface AssignableSystemsResult {
  options: AssignableSystemOption[];
  /** true wenn der Server mehr Treffer hätte (Seite gekappt). */
  truncated: boolean;
  isLoading: boolean;
  isError: boolean;
}

/** Seitengröße der Zuordnen-Suche (list_systems erlaubt max. 200). */
const ASSIGNABLE_PAGE_SIZE = 50;

/**
 * Systeme für die Zuordnen-Combobox via serverseitiger Suche
 * (GET /systems?filter[search]=..., matcht hostname UND display_name) statt
 * einer fixen 200er-Liste. excluded-Systeme werden ausgefiltert: der
 * Zabbix-Ingest lädt nur eligibility != 'excluded' — ein Override auf einem
 * excluded-System bekäme nie Observations. list_systems kennt für
 * filter[scope] nur Gleichheit (kein "alles ausser"), daher clientseitig.
 */
export function useZabbixAssignableSystems(search: string): AssignableSystemsResult {
  const trimmed = search.trim();
  const query = useQuery<SystemList>({
    queryKey: zabbixIntegrationKeys.assignableSystems(trimmed),
    queryFn: () =>
      api.get<SystemList>('/systems', {
        'filter[search]': trimmed || undefined,
        limit: ASSIGNABLE_PAGE_SIZE,
      }),
    staleTime: 30_000,
    // Beim Tippen die vorige Liste stehen lassen (kein Flackern).
    placeholderData: keepPreviousData,
  });

  const systems = (query.data?.data ?? []).filter((s) => s.scope !== 'excluded');
  return {
    options: systems.map((s) => ({
      value: s.id,
      label: s.hostname || s.display_name || s.id,
      hint:
        s.display_name && s.display_name !== s.hostname
          ? s.display_name
          : undefined,
    })),
    truncated: Boolean(query.data?.links?.next),
    isLoading: query.isPending || query.isFetching,
    isError: query.isError,
  };
}

// --- Mutationen ------------------------------------------------------------

function invalidateZabbix(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: zabbixIntegrationKeys.status });
  void qc.invalidateQueries({ queryKey: zabbixIntegrationKeys.unmatched });
}

/** POST /integrations/zabbix/ingest — manueller Sofort-Lauf. */
export function useTriggerZabbixIngest(): ReturnType<
  typeof useMutation<ZabbixIngestRunResponse, Error, void>
> {
  const qc = useQueryClient();
  return useMutation<ZabbixIngestRunResponse, Error, void>({
    mutationFn: () =>
      api.post<ZabbixIngestRunResponse>('/integrations/zabbix/ingest'),
    onSuccess: () => {
      invalidateZabbix(qc);
    },
  });
}

export interface SetZabbixHostRefInput {
  systemId: string;
  /**
   * null = Auto-Match (Override entfernen), '' = System vom Ingest
   * ausschließen, sonst technischer Zabbix-Hostname (host.host).
   */
  zabbixHostRef: string | null;
}

/**
 * Setzt systems.zabbix_host_ref über das bestehende PATCH /systems/{id}
 * (Zuordnen-Combobox bzw. Ignorieren-Button der Unmatched-Liste).
 */
export function useSetZabbixHostRef(): ReturnType<
  typeof useMutation<unknown, Error, SetZabbixHostRefInput>
> {
  const qc = useQueryClient();
  return useMutation<unknown, Error, SetZabbixHostRefInput>({
    mutationFn: ({ systemId, zabbixHostRef }) =>
      api.patch<unknown, { zabbix_host_ref: string | null }>(
        `/systems/${systemId}`,
        { zabbix_host_ref: zabbixHostRef }
      ),
    onSuccess: () => {
      invalidateZabbix(qc);
      // System-Detail/-Listen zeigen das Override-Feld ebenfalls.
      void qc.invalidateQueries({ queryKey: queryKeys.systems.all });
    },
  });
}

// --- Hostliste für das System-Formular ------------------------------------

export interface ZabbixHostsFilter {
  customerId?: string | undefined;
  q?: string | undefined;
  onlyUnassigned?: boolean | undefined;
  limit?: number | undefined;
  enabled?: boolean | undefined;
}

/**
 * GET /integrations/zabbix/hosts — Vorschläge für „Aus Zabbix übernehmen".
 * 503 = Zabbix nicht erreichbar: kein Retry, das Formular bleibt bedienbar.
 */
export function useZabbixHosts(
  filter: ZabbixHostsFilter = {}
): ReturnType<typeof useQuery<ZabbixHostListResponse>> {
  const q = filter.q?.trim() || undefined;
  const onlyUnassigned = filter.onlyUnassigned ?? true;
  const limit = filter.limit ?? 50;
  const params = {
    customer_id: filter.customerId,
    q,
    only_unassigned: onlyUnassigned,
    limit,
  };
  return useQuery<ZabbixHostListResponse>({
    queryKey: zabbixIntegrationKeys.hosts(params),
    queryFn: () =>
      api.get<ZabbixHostListResponse>('/integrations/zabbix/hosts', params),
    staleTime: 120_000,
    placeholderData: keepPreviousData,
    enabled: filter.enabled ?? true,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status === 503) return false;
      return failureCount < 1;
    },
  });
}
